import type { Position } from '@capacitor/geolocation';
import { useEffect, useRef, useState } from 'react';
import type { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';

import { bestFix } from '@/hooks/useGeolocation';

type EntryFix<F extends FieldValues> = Partial<Record<Path<F>, string>>;

/**
 * A new log entry starts where the boat is: `toFields` turns the device fix into field values.
 * Whatever was typed while the GPS settled stays; no fix (denied, off, no sky) leaves the fields
 * to fill by hand. Returns whether the fix is still being looked up.
 */
export function useEntryFix<F extends FieldValues>(
  isNew: boolean,
  { getValues, setValue }: Pick<UseFormReturn<F>, 'getValues' | 'setValue'>,
  toFields: (coords: Position['coords']) => EntryFix<F> | Promise<EntryFix<F>>,
) {
  const [locating, setLocating] = useState(isNew);
  // Read once, on mount: the entry looks itself up a single time.
  const toFieldsRef = useRef(toFields);

  useEffect(() => {
    if (!isNew) return;
    let cancelled = false;
    bestFix()
      .then(({ coords }) => toFieldsRef.current(coords))
      .then((fields) => {
        if (cancelled) return;
        for (const [key, value] of Object.entries(fields) as [Path<F>, string][]) {
          if (value && !getValues(key)) setValue(key, value as PathValue<F, Path<F>>);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLocating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isNew, getValues, setValue]);

  return locating;
}
